import { useState, useEffect } from "preact/hooks";
import { fetchCollectionByGid, fetchCollectionByHandle } from "react-components/CollectionQL";
import { fetchProducts } from "react-components/ProductQL";

// Helper: choose correct fetch function based on input
function getCollectionFetcher(input) {
    if (!input) return null;
    if (input.startsWith("gid://shopify/Collection/")) {
        return fetchCollectionByGid;
    }
    return fetchCollectionByHandle;
}

export function useFlavorLineCollection(flavorLineCollectionGidOrHandle) {
    const [otherFlavors, setOtherFlavors] = useState([]);
    const [otherFlavorsLoading, setOtherFlavorsLoading] = useState(false);

    useEffect(() => {
        setOtherFlavors([]);
        setOtherFlavorsLoading(false);

        if (!flavorLineCollectionGidOrHandle) return;

        setOtherFlavorsLoading(true);

        const fetchCollection = getCollectionFetcher(flavorLineCollectionGidOrHandle);
        if (!fetchCollection) {
            setOtherFlavors([]);
            setOtherFlavorsLoading(false);
            return;
        }

        let ignore = false;

        let earlyAccessSeconds = 0;
        if (typeof window !== "undefined" && window.cirkulServices) {
            if (window.cirkulServices.allowEarlyAccess) {
                earlyAccessSeconds = 2 * 60 * 60;
            }
            if (window.cirkulServices.vipEarlyAccess) {
                earlyAccessSeconds = 14 * 60 * 60;
            }
        }

        async function fetchOtherFlavors() {
            const collection = await fetchCollection(flavorLineCollectionGidOrHandle);
            if (!collection || !collection.products) {
                setOtherFlavors([]);
                setOtherFlavorsLoading(false);
                return;
            }
            const nowTimestamp = Math.floor(Date.now() / 1000);
            const filtered = collection.products.filter((p) => {
                const tags = Array.isArray(p.tags) ? p.tags : (typeof p.tags === "string" ? p.tags.split(',') : []);
                if (tags.includes('Not For Sale')) return false;
                if (Array.isArray(p.collections) && p.collections.includes('Variety Packs')) return false;
                let launch = Number(p.metafields?.launch_timestamp || 0);
                if (earlyAccessSeconds > 0) launch -= earlyAccessSeconds;
                return launch <= nowTimestamp;
            });

            const batchSize = 8;
            let allProducts = [];
            for (let i = 0; i < filtered.length; i += batchSize) {
                if (ignore) return;
                const batch = filtered.slice(i, i + batchSize);
                const ids = batch.map(p => {
                    if (p.id && String(p.id).startsWith('gid://')) return p.id;
                    return `gid://shopify/Product/${p.id}`;
                });
                const products = await fetchProducts(ids);
                allProducts = [...allProducts, ...products];
                setOtherFlavors([...allProducts]);
            }
            setOtherFlavorsLoading(false);
        }
        fetchOtherFlavors();

        return () => { ignore = true; };
    }, [flavorLineCollectionGidOrHandle]);

    return { otherFlavors, otherFlavorsLoading };
}
